import type { ReactElement } from 'react';
import { Bot, X, Undo2 } from 'lucide-react';
import type {
  NetworkConnectionListItem,
  NetworkConnectionStatus,
  NetworkDomainHost,
  ShadowAiConfidence,
} from '@kirin/api-client/contracts/shadow-ai';
import { confidenceLabelLabels } from '@kirin/api-client/contracts/shadow-ai';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { hostDecisionSource, isGroupedRow } from './grouping';

/**
 * The AI-confidence hint ("Possible AI"), shown beside the Host. KIR-4588
 * moved it off the Status cell — the signal is about the host the user
 * classifies. Pending rows only: once classified, the human decision
 * supersedes the heuristic.
 */
export function ConfidenceBadge({
  connection,
}: {
  connection: NetworkConnectionListItem;
}): ReactElement | null {
  const { status, confidence } = connection;
  if (status !== 'pending' || !confidence) return null;
  return <ConfidenceLabelBadge confidence={confidence} />;
}

/**
 * The badge itself, without the "only while pending" rule.
 *
 * Split out for the per-HOST signal (KIR-4948): a host under a grouped row
 * carries its own confidence but no `NetworkConnectionListItem` to read a
 * status from, and the caller there decides when to show it. Keeping one
 * component for the colours means the two cannot drift apart.
 */
export function ConfidenceLabelBadge({
  confidence,
}: {
  confidence: ShadowAiConfidence;
}): ReactElement {
  return (
    <Badge
      variant="outline"
      className={cn(
        'text-xs font-normal shrink-0',
        confidence.label === 'likely_ai'
          ? 'border-green-600/40 text-green-700 dark:text-green-400'
          : confidence.label === 'possible_ai'
            ? 'border-amber-600/40 text-amber-700 dark:text-amber-400'
            : 'border-border text-muted-foreground'
      )}
    >
      {confidenceLabelLabels[confidence.label]}
    </Badge>
  );
}

/**
 * The one classification label beside a host under a grouped row (KIR-5168).
 *
 * Before this the slot showed only the confidence guess, and only while the
 * host was unreviewed, so a decided host had nothing there and an override was
 * told apart by a chip squeezed beside the Status menu. Now the slot always
 * states what the host is and who said so:
 *
 * - unreviewed: the confidence guess (Likely / Possible / Unlikely AI);
 * - identified: AI, AI (builtin) or AI (manual override);
 * - dismissed: Not AI, Not AI (builtin) or Not AI (manual override).
 *
 * The human or built-in decision supersedes the heuristic, so once decided the
 * confidence is not shown.
 */
export function HostClassificationBadge({
  host,
}: {
  host: Pick<
    NetworkDomainHost,
    | 'effectiveDecision'
    | 'effectiveSource'
    | 'decision'
    | 'overridden'
    | 'confidence'
  >;
}): ReactElement | null {
  if (host.effectiveDecision === 'unreviewed') {
    return host.confidence ? (
      <ConfidenceLabelBadge confidence={host.confidence} />
    ) : null;
  }

  const source = hostDecisionSource(host);
  const base = host.effectiveDecision === 'identified' ? 'AI' : 'Not AI';
  const label =
    source === 'override'
      ? `${base} (manual override)`
      : source === 'builtin'
        ? `${base} (builtin)`
        : base;
  const title =
    source === 'override'
      ? 'Set on this host, so a domain-level action will not change it'
      : source === 'builtin'
        ? 'Kirin already knows this host'
        : "Follows the domain's status";

  return (
    <Badge
      variant="outline"
      title={title}
      className={cn(
        'text-xs font-normal shrink-0',
        host.effectiveDecision === 'identified'
          ? 'border-blue-600/40 text-blue-700 dark:text-blue-400'
          : 'border-border text-muted-foreground'
      )}
    >
      {label}
    </Badge>
  );
}

/**
 * Status pill. The AI-confidence hint used to live here too; it now sits on
 * the Host cell via {@link ConfidenceBadge} (KIR-4588).
 */
export function NetworkStatusBadge({
  connection,
}: {
  connection: NetworkConnectionListItem;
}): ReactElement {
  const { status, ignoreSource, identificationSource, hostStatusMixed } =
    connection;

  // KIR-4948: the hosts under this row do not agree, so there is no single
  // status to show. Deliberately replaces the pill rather than sitting beside
  // it -- showing "AI Agent" next to "Mixed" would state the service decision
  // as though it described every host, which is the thing that is not true.
  // Amber, matching the mockup: this is "open me", not an error.
  //
  // Gated on the row opening onto a HOST TABLE, not on it being expandable --
  // every row here expands, since no `canExpand` is passed. The disagreement
  // is read from the service tables, which carry no user dimension and answer
  // organization-wide, so a row with no host list would get a badge saying
  // something is wrong with no way to look. For them the service status is the
  // honest answer, because it is the only one their row can account for.
  //
  // This MUST be the same test the expansion uses. It was `hostCount > 1` --
  // correct while that was also what decided expansion, and wrong the moment
  // the expansion moved to `grouped`: a grouped service with one visible host
  // opens its host table, including the every-host-contradicts-the-service
  // case, while the collapsed row went on showing the service status alone.
  if (hostStatusMixed && isGroupedRow(connection)) {
    return (
      <Badge
        className="text-xs bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200"
        variant="secondary"
        title="This domain's hosts do not all have the same status. Expand the row to see each one."
      >
        Mixed
      </Badge>
    );
  }

  const variants: Record<NetworkConnectionStatus, string> = {
    approved: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    flagged: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    pending: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
    ignored: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
  };

  const label = ((): string => {
    if (status === 'approved' || status === 'flagged') {
      return identificationSource === 'global'
        ? 'AI Agent (builtin)'
        : 'AI Agent';
    }
    if (status === 'ignored') {
      return ignoreSource === 'global' ? 'Dismissed (builtin)' : 'Dismissed';
    }
    return 'Unreviewed';
  })();

  return (
    <Badge className={cn('text-xs', variants[status])} variant="secondary">
      {label}
    </Badge>
  );
}

/** Per-row action callbacks + pending flags for the trailing Actions column. */
export interface NetworkDomainsActions {
  onIdentify: (connection: NetworkConnectionListItem) => void;
  onDismiss: (domain: string) => void;
  onRevertIgnore: (domain: string) => void;
  onRevertClassified: (domain: string) => void;
  isClassifying: boolean;
  isReverting: boolean;
}

/** Identify / Dismiss (pending) or Revert (classified / dismissed) buttons. */
export function NetworkActionsCell({
  row,
  actions,
}: {
  row: NetworkConnectionListItem;
  actions: NetworkDomainsActions;
}): ReactElement {
  const canRevert =
    ((row.status === 'approved' || row.status === 'flagged') &&
      row.identificationSource !== 'global') ||
    (row.status === 'ignored' && row.ignoreSource === 'org');

  if (row.status === 'pending') {
    return (
      <div className="flex items-center justify-end gap-1">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-blue-600 hover:text-blue-700 hover:bg-blue-100 dark:hover:bg-blue-950"
          onClick={() => actions.onIdentify(row)}
          disabled={actions.isClassifying}
          title="Identify as AI Agent"
        >
          <Bot className="h-4 w-4 mr-1" />
          Identify
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-muted-foreground hover:text-foreground"
          onClick={() => actions.onDismiss(row.domain)}
          disabled={actions.isClassifying}
          title="Dismiss (not AI)"
        >
          <X className="h-4 w-4 mr-1" />
          Dismiss
        </Button>
      </div>
    );
  }

  if (canRevert) {
    return (
      <div className="flex items-center justify-end">
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground hover:text-foreground"
          onClick={() =>
            row.status === 'ignored'
              ? actions.onRevertIgnore(row.domain)
              : actions.onRevertClassified(row.domain)
          }
          disabled={actions.isReverting}
          title="Revert to unreviewed"
        >
          <Undo2 className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  return <span className="text-muted-foreground">—</span>;
}
